import { useEffect, useState, useLayoutEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { useLazyQuery } from "@apollo/client";

import "./JobListPage.scss";
import { SortSettings } from "../../../component/sortable-table/SortableTable";
import { useDarkModeCheck } from "../DashboardLayout";
import {
  inputDateFormat,
  previousMonthRangeSelect,
  sortTableFunction,
} from "../../../service/utils/functions";
import {
  excelFileExtension,
  excelFileType,
  jobStatusSelectorList,
  SortOrder,
  tableIssuesHeader,
  tableJobsHeader,
  tableTippingJobsHeader,
} from "../../../service/utils/constants";
import {
  GET_CUSTOMERS,
  GET_JOBS_ISSUES,
  GET_JOBS_TIPPING,
} from "../../../service/graphql/query";
import {
  CustomerDataEntry,
  JobStatusDataEntry,
  SiteDataEntry,
  WasteTypeDataEntry,
} from "../../../service/utils/interfaces";
import * as Storage from "../../../service/storage";
import {
  jobListFormat,
  issueListFormat,
  jobTippingListFormat,
} from "../../../service/utils/parsers";
import JobListUI from "./JobListUI";

const FileSaver = require("file-saver");

const JobListPage = () => {
  const { darkMode } = useDarkModeCheck();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() =>
    previousMonthRangeSelect()
  );
  const [startDate, endDate] = dateRange;

  // 1. Add new loading state for initial customer/filter data
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDataEntry>();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<SiteDataEntry[]>([]);
  const [selectedSites, setSelectedSites] = useState<SiteDataEntry[]>();
  const [wasteTypesPerCustomer, setWasteTypePerCustomer] = useState<
    WasteTypeDataEntry[]
  >([]);
  const [selectedWasteTypes, setSelectedWasteTypes] =
    useState<WasteTypeDataEntry[]>();
  const [selectedStatus, setSelectedStatus] = useState<JobStatusDataEntry[]>();

  const [headerJobsLine, setHeaderJobsLine] = useState<string[]>([]);
  const [jobsContent, setJobsContent] = useState<(string | number)[][]>([]);
  const [filteredJobsContent, setFilteredJobsContent] = useState<
    (string | number)[][]
  >([]);
  const [headerIssuesLine, setHeaderIssuesLine] = useState<string[]>([]);
  const [issuesContent, setIssuesContent] = useState<(string | number)[][]>([]);
  const [filteredIssuesContent, setFilteredIssuesContent] = useState<
    (string | number)[][]
  >([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState<string>("");
  const [reordering, setReordering] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [isTippingUser, setIsTippingUser] = useState(false);

  const [getCustomers, { data: customersData }] = useLazyQuery(GET_CUSTOMERS);
  const [getJobIssueLists, { data: jobsIssuesData }] =
    useLazyQuery(GET_JOBS_ISSUES);
  const [getJobTippingLists, { data: jobsTippingData }] =
    useLazyQuery(GET_JOBS_TIPPING);

  const [width, setWidth] = useState(0);

  // This effect implements the loading timeout.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    // If loading is still in progress, start a 15-second timeout.
    if (isInitialLoading) {
      timeoutId = setTimeout(() => {
        console.warn("Data loading timed out. Clearing cache and reloading.");
        // Clear the potentially corrupted user data cache
        Storage.USER_DATA.remove().then(() => {
          // Force a full page reload to fetch fresh data
          window.location.reload();
        });
      }, 5000); // 5-second timeout
    }

    // Cleanup function: If the component unmounts or if loading completes
    // successfully, clear the timeout to prevent it from firing.
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitialLoading]); // This effect depends only on the loading state.

  // Add this useEffect to handle page restoration
  useEffect(() => {
    if ((document as any).wasDiscarded) {
      console.warn("JobListPage was discarded and restored. Forcing a data refresh.");
      // Clear the potentially stale user data and reload to ensure a clean state.
      Storage.USER_DATA.remove().then(() => {
        window.location.reload();
      });
    }
  }, []); // Empty dependency array ensures this runs only once on mount.

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  const checkUserPermissions = useCallback(async (customers: any[]) => {
    if (customers && customers.length > 0) {
      const user = customers[0];
      setCustomerList(customers);
      const filter = await Storage.FILTER_DATA.get();
      const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
      setSelectedCustomer(customers[idx]);
      const fetchedTippingUserData = user.category === "Tipping";
      setIsTippingUser(fetchedTippingUserData);
      setHeaderJobsLine(
        fetchedTippingUserData ? tableTippingJobsHeader : tableJobsHeader
      );
      setHeaderIssuesLine(
        fetchedTippingUserData ? tableTippingJobsHeader : tableIssuesHeader
      );
      setReadyToFetch(true);
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    Storage.FILTER_DATA.get().then((filter) => {
      const start =
        filter.startDate.length > 0 ? new Date(filter.startDate) : null;
      const end = filter.endDate.length > 0 ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      else {
        const updateRange = previousMonthRangeSelect();
        setDateRange(updateRange);
      }
    });

    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          checkUserPermissions(customerList);
        } else {
          getCustomers({ context: { apiName: "middleware" } });
        }
      })
      .catch((_) => {
        getCustomers({ context: { apiName: "middleware" } });
      });

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getCustomers, checkUserPermissions]);

  useEffect(() => {
    if (selectedCustomer) {
      // This ensures the available sites are set first.
      const sites = selectedCustomer?.sites ?? [];
      const wastes = selectedCustomer?.wastes ?? [];
      setSitesPerCustomer(sites);
      setWasteTypePerCustomer(wastes);

      // Now, safely restore the selection from the populated lists.
      Storage.FILTER_DATA.get().then((filter) => {
        if (filter.siteIndex) {
          const siteList: any[] = [];
          sites.forEach((site: any, idx: number) => {
            if (filter.siteIndex.includes(idx)) {
              siteList.push(site);
            }
          });
          setSelectedSites(siteList);
        } else {
          setSelectedSites([]); // Explicitly set to empty array if nothing is stored
        }
        if (filter.wasteTypeIndex) {
          const wasteTypeList: any[] = [];
          wastes.forEach((waste: any, idx: number) => {
            if (filter.wasteTypeIndex.includes(idx)) {
              wasteTypeList.push(waste);
            }
          });
          setSelectedWasteTypes(wasteTypeList);
        } else {
          setSelectedWasteTypes([]); // Explicitly set to empty array
        }
      });
      setSelectedStatus([]);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (
      readyToFetch &&
      selectedSites &&
      selectedWasteTypes &&
      selectedStatus &&
      dateRange &&
      dateRange[0] &&
      dateRange[1]
    ) {
      setLoadingData(true);
      let siteIdList: any[] = [];
      if (selectedSites.length > 0)
        siteIdList = selectedSites.map((el: any) => el.value);
      else siteIdList = sitesPerCustomer.map((el: any) => el.value);

      let wasteTypeIdList: any[] = [];
      if (selectedWasteTypes.length > 0)
        wasteTypeIdList = selectedWasteTypes.map((el: any) => el.value);
      else wasteTypeIdList = wasteTypesPerCustomer.map((el: any) => el.value);

      let statusList: any[] = [];
      if (selectedStatus.length > 0)
        statusList = selectedStatus.map((el: any) => el.value);
      else statusList = jobStatusSelectorList.map((status) => status.value);

      if (!isTippingUser) {
        getJobIssueLists({
          variables: {
            input: {
              siteList: siteIdList,
              wasteList: wasteTypeIdList,
              statusList: statusList,
              startDate: inputDateFormat(dateRange[0] as Date),
              endDate: inputDateFormat(dateRange[1] as Date),
            },
          },
          context: { apiName: "middleware" },
        });
      } else {
        getJobTippingLists({
          variables: {
            input: {
              documentId: selectedCustomer?.value,
              startDate: inputDateFormat(dateRange[0] as Date),
              endDate: inputDateFormat(dateRange[1] as Date),
            },
          },
          context: { apiName: "middleware" },
        });
      }
    }
  }, [
    readyToFetch,
    selectedCustomer,
    selectedSites,
    selectedWasteTypes,
    selectedStatus,
    sitesPerCustomer,
    wasteTypesPerCustomer,
    dateRange,
    isTippingUser,
    getJobIssueLists,
    getJobTippingLists,
  ]);

  useEffect(() => {
    if (customersData && customersData.getCustomers) {
      manageCustomersData(customersData.getCustomers);
      checkUserPermissions(customersData.getCustomers);
    }
  }, [customersData, checkUserPermissions]);

  const manageCustomersData = async (customersData: any) => {
    const tempCustomerList: CustomerDataEntry[] = [];
    customersData.forEach((elem: any) => {
      const customerSites: SiteDataEntry[] = [];
      const customerWastes: WasteTypeDataEntry[] = [];
      elem.sites?.forEach((site: any) => {
        const siteId = parseInt(site.id);
        customerSites.push({
          value: siteId,
          label: site.customerSite,
        });
      });
      elem.wastes?.forEach((wt: any) => {
        const wasteId = parseInt(wt.wasteTypeId);
        customerWastes.push({
          value: wasteId,
          label: wt.wasteType,
        });
      });
      tempCustomerList.push({
        value: elem.id,
        label: elem.customer,
        category: elem.category,
        sites: customerSites,
        wastes: customerWastes,
      });
    });
    Storage.USER_DATA.set(tempCustomerList);
  };

  useEffect(() => {
    if (jobsIssuesData && jobsIssuesData.getJobIssueDocList) {
      manageJobIssuesData(jobsIssuesData);
    }
  }, [jobsIssuesData]);

  const manageJobIssuesData = (jobsIssuesData: any) => {
    const parsedJobs = jobListFormat(jobsIssuesData.getJobIssueDocList.jobs);
    const parsedIssues = issueListFormat(
      jobsIssuesData.getJobIssueDocList.issues
    );
    setJobsContent(parsedJobs);
    setFilteredJobsContent(parsedJobs);
    setIssuesContent(parsedIssues);
    setFilteredIssuesContent(parsedIssues);
    setLoadingData(false);
  };

  useEffect(() => {
    if (jobsTippingData && jobsTippingData.getJobTippingList) {
      manageJobTippingData(jobsTippingData);
    }
  }, [jobsTippingData]);

  const manageJobTippingData = (jobsTippingData: any) => {
    const parsedJobs = jobTippingListFormat(
      jobsTippingData.getJobTippingList.jobs
    );
    const parsedCardboard = jobTippingListFormat(
      jobsTippingData.getJobTippingList.cardboard
    );
    setJobsContent(parsedJobs);
    setFilteredJobsContent(parsedJobs);
    setIssuesContent(parsedCardboard);
    setFilteredIssuesContent(parsedCardboard);
    setLoadingData(false);
  };

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      if (currentTab > 0) {
        const reorderedTable = issuesContent.sort((a, b) =>
          sortTableFunction(a, b, sortSettings)
        );
        setFilteredIssuesContent(reorderedTable);
      } else {
        const reorderedTable = jobsContent.sort((a, b) =>
          sortTableFunction(a, b, sortSettings)
        );
        setFilteredJobsContent(reorderedTable);
      }
    }
  }, [sortSettings, reordering, currentTab, issuesContent, jobsContent]);

  const downloadExcelTable = () => {
    const content =
      currentTab > 0
        ? filteredIssuesContent
        : filteredJobsContent.map((row) => {
            if (isTippingUser) return row;
            const lastItem = row[row.length - 1];
            if (typeof lastItem === "string") {
              row[row.length - 1] =
                lastItem.length > 0 ? "<document link>" : "";
            } else {
              row[row.length - 1] = "";
            }
            return row;
          });
    const headerLine = currentTab > 0 ? headerIssuesLine : headerJobsLine;
    const tableToPrint = [[], ...content];
    const ws = XLSX.utils.aoa_to_sheet(tableToPrint);
    XLSX.utils.sheet_add_aoa(ws, [headerLine], { origin: "A1" });
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: excelFileType });
    FileSaver.saveAs(
      data,
      currentTab > 0
        ? isTippingUser
          ? "Cardboards"
          : "Job Issues"
        : "Job List" + excelFileExtension
    );
  };

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  // 4. Implement the loading guard
  if (isInitialLoading) {
    return <div>Loading...</div>;
  }

  return (
    <JobListUI
      isTippingUser={isTippingUser}
      customerList={customerList}
      sitesPerCustomer={sitesPerCustomer}
      wasteTypesPerCustomer={wasteTypesPerCustomer}
      startDate={startDate}
      endDate={endDate}
      currentTab={currentTab}
      loadingData={loadingData}
      headerJobsLine={headerJobsLine}
      filteredJobsContent={filteredJobsContent}
      sortSettings={sortSettings}
      searchParam={searchParam}
      headerIssuesLine={headerIssuesLine}
      filteredIssuesContent={filteredIssuesContent}
      resetTable={resetTable}
      setSelectedCustomer={setSelectedCustomer}
      setSelectedSites={setSelectedSites}
      setSelectedWasteTypes={setSelectedWasteTypes}
      setSelectedStatus={setSelectedStatus}
      setDateRange={setDateRange}
      setCurrentTab={setCurrentTab}
      downloadExcelTable={downloadExcelTable}
      changeSortAction={changeSortAction}
      changeSearchParam={changeSearchParam}
      darkMode={darkMode}
      width={width}
    />
  );
};

export default JobListPage;
